import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { RequestApi } from ".";
import { useAppContext } from "../AppProviders";
import {
  getAnalyticsDataMockMultiDistribution,
  getBlueprintVerifyMock,
  getCompaniesApiMock,
  getCompaniesWithBlueprintInfoMock,
  getCompanyInfoListMock,
  getCompanyListMock,
} from "../__mocks__/companiesList";
import {
  createCompanyMock,
  getCompanyBaseMock,
  getReportAnalyticsChartsMock,
  getSettingTypesMock,
} from "../__mocks__/companyOps";
import {
  BehaviorInfoMinimal,
  BehaviorKitMinimalInfo,
  BlueprintMinimalInfo,
} from "./Blueprints";
import { CacheKeys } from "./Cache";
import {
  AnalyticsData,
  BehaviorKit,
  BehaviorKitUpdate,
  CompaniesApi,
  CompaniesWithFilter,
  CompanyCreationResponse,
  CompanyCreationSetting,
  CompanyDetail,
  CompanyEx,
  CompanyFilterInfoApi,
  CompanyInfoApi,
  CompanyListInputApi,
  CompanyMetricsRequest,
  CompanyUpdateInput,
  CompanyWithBlueprintInfo,
  CreateCompanyInput,
  PDFTemplate,
  ReportAnalyticsChartsResponse,
  ReportsLookupExportRequest,
  ReportsLookupRequest,
  ReportsLookupResponse,
  SETTING_TYPES_ENUM,
  SettingType,
  SubCompanyListInputApi,
} from "./Companies";
import { API_URL, HARDCODED_ACTOR_UUID, isMocksEnabled } from "./helpers";
import { intersection } from "lodash";
import { resolveAndDownload } from "../utils";
import { useNavigate } from "react-router-dom";

export function useCreateCompany() {
  const { requestApi, refreshCache } = useAppContext();

  const createCompany = async (
    companyInput: CreateCompanyInput
  ): Promise<CompanyCreationResponse> => {
    if (isMocksEnabled()) {
      return createCompanyMock(companyInput);
    }

    return requestApi
      .post(`${API_URL}/company_and_company_settings`, companyInput)
      .then((response) => {
        refreshCache(CacheKeys.parentCompanies);
        refreshCache(CacheKeys.companies);
        refreshCache(CacheKeys.companiesInfo);
        refreshCache(CacheKeys.blueprints);
        return response.data;
      })
      .catch((e) => {
        if (e.response.status === 409) throw Error(e.response.data.detail);
        throw Error(e);
      });
  };

  const mutationResult = useMutation(
    (companyInput: CreateCompanyInput) => createCompany(companyInput),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useCompaniesFetchAll() {
  const { requestApi, cache, refreshCache } = useAppContext();

  const fetchCompaniesMinimalList = async (): Promise<CompaniesApi[]> => {
    if (isMocksEnabled()) {
      return await getCompaniesApiMock();
    }

    if (
      cache.current.companies?.items &&
      Array.isArray(cache.current.companies?.items)
    ) {
      return cache.current.companies.items;
    }

    return requestApi
      .get(`${API_URL}/companydal/companies`)
      .then((response) => {
        let data = (response?.data as CompaniesApi[]) || [];
        data.sort((a, b) => a.name.localeCompare(b.name));
        refreshCache(CacheKeys.companies, data);
        return data;
      });
  };

  const mutationResult = useMutation(() => fetchCompaniesMinimalList(), {});

  return {
    ...mutationResult,
  };
}

export function useGetParentCompanyInfoList() {
  const { requestApi, cache, refreshCache, companiesFromAuth } =
    useAppContext();

  const getParentCompanies = async (
    input: CompanyListInputApi
  ): Promise<CompaniesWithFilter> => {
    if (companiesFromAuth.allCompanies) {
      // cache only applies in case of allCompanies
      const currentCache = cache.current.parentCompanies?.find((element) => {
        return element.page === input.page && element.search === input.search;
      });
      if (currentCache?.items && !Array.isArray(currentCache?.items)) {
        return currentCache.items;
      }
      let endpoint = input.search
        ? "search_all_companies"
        : "companydal/parent_companies";
      return requestApi
        .get(`${API_URL}/${endpoint}`, input)
        .then((response) => {
          refreshCache(
            CacheKeys.parentCompanies,
            response.data,
            input.page,
            "companies_search",
            input.search
          );
          return response.data;
        });
    } else {
      // do not cache requests for specific companies for this user
      // we are relying on the endpoint to filter by actor_uuid of user
      return requestApi
        .get(`${API_URL}/my_companies`, input)
        .then((response) => {
          return response.data;
        });
    }
  };

  const mutationResult = useMutation(
    (input: CompanyListInputApi) => getParentCompanies(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useMyCompaniesMinimal() {
  const { requestApi } = useAppContext();
  const [companies, setCompanies] = useState<CompanyFilterInfoApi[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    const PAGE_SIZE = 50;

    async function fetchAll() {
      setIsLoading(true);
      setError(null);
      const all: CompanyFilterInfoApi[] = [];
      let page = 1;

      try {
        while (true) {
          const input: Record<string, number | boolean | undefined> = {
            page,
            size: PAGE_SIZE,
            minimal: true,
          };
          const response = isMocksEnabled()
            ? await getCompanyListMock({ page, size: PAGE_SIZE })
            : await requestApi
                .get(`${API_URL}/my_companies`, input)
                .then((r) => r.data);

          const data = response as CompaniesWithFilter;
          const items = Array.isArray(data?.items) ? data.items : [];
          all.push(...items);

          const total =
            typeof data?.total === "number" ? data.total : all.length;
          if (items.length < PAGE_SIZE || all.length >= total) break;
          page++;
        }

        if (!cancelled) {
          const sorted = [...all].sort((a, b) => a.name.localeCompare(b.name));
          setCompanies(sorted);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [requestApi]);

  return { companies, isLoading, error };
}

export function useGetSubCompanyInfoList() {
  const { requestApi } = useAppContext();

  const getSubCompanies = async (
    input: SubCompanyListInputApi
  ): Promise<CompaniesWithFilter> => {
    return requestApi
      .get(`${API_URL}/search_all_companies`, input)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: SubCompanyListInputApi) => getSubCompanies(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetCompanyInfoList() {
  const { requestApi } = useAppContext();

  const getCompanySummary = async (
    input: CompanyListInputApi
  ): Promise<CompaniesWithFilter> => {
    if (isMocksEnabled()) {
      return await getCompanyListMock(input);
    }

    return requestApi
      .get(`${API_URL}/subcompanies_multifilter`, input)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (input: CompanyListInputApi) => getCompanySummary(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export type CompaniesWithBlueprintInput = {
  company_uuids: string[];
  blueprintsList: BlueprintMinimalInfo[];
};

export function blueprintsToBlueprintsInfo(
  blueprintsUuids: string[],
  blueprintList: BlueprintMinimalInfo[]
) {
  const mappedBlueprints = blueprintsUuids?.map((item) => {
    return blueprintList.find((pivot) => pivot.blueprint_uuid === item);
  }) as BlueprintMinimalInfo[];

  const blueprintsInfo: BlueprintMinimalInfo[] = mappedBlueprints.filter(
    (item) => item
  ) as BlueprintMinimalInfo[];
  return blueprintsInfo;
}

const companyInfoListFetch = async (
  list: string[],
  requestApi: RequestApi
): Promise<CompanyInfoApi[]> => {
  if (isMocksEnabled()) {
    return await getCompanyInfoListMock(list);
  }

  return requestApi.post(`${API_URL}/companies_info`, list).then((response) => {
    return response.data;
  });
};

const companyInfoExFetch = async (
  companyUUID: string,
  requestApi: RequestApi
): Promise<CompanyEx> => {
  return requestApi
    .get(`${API_URL}/company/ex/${companyUUID}?use_template_definition=true`)
    .then((response) => {
      return response.data;
    });
};

export function useGetCompaniesInfoList() {
  const { requestApi, refreshCache, cache } = useAppContext();

  async function loadCompaniesInfo(
    list: string[],
    requestApi: RequestApi
  ): Promise<CompanyInfoApi[]> {
    let cacheInfoItems = cache?.current.companiesInfo?.items as
      | CompanyInfoApi[]
      | undefined;
    if (
      cacheInfoItems &&
      cacheInfoItems.length > 0 &&
      intersection(
        cacheInfoItems.map((item: CompanyInfoApi) => item.company_uuid),
        list
      ).length === list.length
    ) {
      return cacheInfoItems;
    }
    return companyInfoListFetch(list, requestApi);
  }

  const mutationResult = useMutation<CompanyInfoApi[], unknown, string[]>(
    (list: string[]) => {
      return loadCompaniesInfo(list, requestApi).then((data) => {
        refreshCache(CacheKeys.companiesInfo, data);
        return data;
      });
    },
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetCompanyWithBlueprint() {
  const { requestApi } = useAppContext();
  const getCompaniesWithBlueprintInfo = async (
    input: CompaniesWithBlueprintInput
  ): Promise<CompanyWithBlueprintInfo[]> => {
    if (isMocksEnabled()) {
      return await getCompaniesWithBlueprintInfoMock(input);
    }
    const list = input.company_uuids.map((item) => item);
    const companiesInfo = await companyInfoListFetch(list, requestApi);
    if (!companiesInfo) return [];

    const companies: CompanyWithBlueprintInfo[] = companiesInfo.map(
      (company) => {
        return {
          company_uuid: company.company_uuid ?? "",
          name: company.name,
          client_qa: company.client_qa ? true : false,
          internal_qa: company.internal_qa ? true : false,
          cqa_autocomplete_days: company.cqa_autocomplete_days ?? 14,
          blueprintsInfo: blueprintsToBlueprintsInfo(
            company.blueprints ?? [],
            input.blueprintsList
          ),
          default_behavior_kit: company.default_behavior_kit,
          auto_run_instant_fit: company.auto_run_instant_fit,
          additional_web_search: company.additional_web_search,
          web_content_translation: company.web_content_translation,
        };
      }
    );
    return companies;
  };

  const mutationResult = useMutation(
    (input: CompaniesWithBlueprintInput) =>
      getCompaniesWithBlueprintInfo(input),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useLoadSettingTypes() {
  const { requestApi } = useAppContext();

  const getSettingTypes = async (): Promise<SettingType[]> => {
    if (isMocksEnabled()) {
      return await getSettingTypesMock();
    }
    return requestApi.get(`${API_URL}/setting_types`).then((response) => {
      return response.data;
    });
  };

  const mutationResult = useMutation(() => getSettingTypes(), {});

  return {
    ...mutationResult,
  };
}

export function useGetCompanyBase() {
  const { requestApi } = useAppContext();

  async function getCompanyBase(companyUUID: string): Promise<CompaniesApi> {
    if (isMocksEnabled()) {
      return getCompanyBaseMock(companyUUID);
    }

    return requestApi
      .get(`${API_URL}/companydal/companies/${companyUUID}`)
      .then((response) => {
        return response.data;
      });
  }

  const mutationResult = useMutation(
    (companyUUID: string) => getCompanyBase(companyUUID),
    {}
  );
  return mutationResult;
}

export function useGetReportAnalyticsCharts(companyUUID: string) {
  const { requestApi } = useAppContext();

  async function getReportAnalyticsCharts(): Promise<ReportAnalyticsChartsResponse> {
    if (isMocksEnabled()) {
      return getReportAnalyticsChartsMock();
    }

    return requestApi
      .get(
        `${API_URL}/companydal/companies/${companyUUID}/report-analytics-charts`
      )
      .then((response) => response.data);
  }

  return useQuery(
    ["reportAnalyticsCharts", companyUUID],
    getReportAnalyticsCharts,
    {
      enabled: Boolean(companyUUID),
      refetchOnWindowFocus: false,
      retry: false,
    }
  );
}

export function useGetCompanyWithDetails() {
  const [detail, setDetail] = useState<CompanyDetail>({
    isChild: false,
    name: "",
    notes: "",
    settings: [],
    loaded: false,
    external_id: "",
    review: true,
    chat_support: true,
    instant_fit_subscriber: false,
    consent_forms: false,
    callback_config: {},
    client_qa: false,
    internal_qa: false,
    cqa_autocomplete_days: undefined,
    default_behavior_kit: undefined,
    default_behavior_kit_assigned_by: undefined,
    default_behavior_kit_assigned_by_name: undefined,
    default_blueprint: undefined,
    auto_run_instant_fit: false,
    additional_web_search: false,
    web_content_translation: false,
  });
  const [blueprints, setBlueprints] = useState<BlueprintMinimalInfo[]>([]);
  const [behaviorKits, setBehaviorKits] = useState<BehaviorKitMinimalInfo[]>(
    []
  ); // update with proper behavirMinimal def

  const {
    isLoading: isLoadingCompanyInfo,
    data: companyInfoList,
    mutate: loadCompanyInfoList,
    error: errorCompanyInfoList,
    reset: resetCompanyInfo,
  } = useGetCompaniesInfoList();

  const {
    isLoading: isLoadingCompanyEx,
    mutate: loadCompanyExtended,
    data: companyExtended,
    error: errorCompanyExtended,
    reset: resetCompanyEx,
  } = useGetCompanyEx();

  const mutate = useCallback(
    (companyUUID: string) => {
      loadCompanyInfoList([companyUUID]);
      loadCompanyExtended(companyUUID);
    },
    [loadCompanyInfoList, loadCompanyExtended]
  );

  useEffect(() => {
    resetCompanyInfo();
    resetCompanyEx();
  }, [resetCompanyInfo, resetCompanyEx]);

  useEffect(() => {
    if (!companyExtended) return;
    const blueprintUUIDs: string[] = Object.keys(companyExtended.blueprints);
    const behaviorKitsUUIDs: string[] = Object.keys(
      companyExtended.behavior_kits
    );
    setBlueprints(
      blueprintUUIDs.map((key) => ({
        blueprint_uuid: key,
        name: companyExtended.blueprints[key],
        report_type: companyExtended.blueprints[key].report_type,
      }))
    );
    setBehaviorKits(
      behaviorKitsUUIDs.map((key) => ({
        behavior_kit_uuid: key,
        name: companyExtended.behavior_kits[key],
      }))
    );
  }, [companyExtended]);

  useEffect(() => {
    const first = companyInfoList?.find((item) => item.company_uuid);

    if (first && companyExtended) {
      const companyDetail: CompanyDetail = {
        name: companyExtended.name,
        notes: companyExtended.notes,
        isChild: !!companyExtended.parent_company_id || false,
        settings: first.company_settings,
        loaded: true,
        external_id: companyExtended.external_id,
        review: companyExtended.review,
        template: companyExtended.template,
        chat_support: companyExtended.chat_support,
        instant_fit_subscriber: companyExtended.instant_fit_subscriber,
        consent_forms: companyExtended.consent_forms,
        callback_config: companyExtended.callback_config,
        pdfTemplate: companyExtended.template_definition,
        client_qa: companyExtended.client_qa,
        internal_qa: companyExtended.internal_qa,
        cqa_autocomplete_days: companyExtended.cqa_autocomplete_days,
        default_behavior_kit: companyExtended.default_behavior_kit,
        default_behavior_kit_assigned_by:
          companyExtended.default_behavior_kit_assigned_by,
        default_behavior_kit_assigned_by_name:
          companyExtended.default_behavior_kit_assigned_by_name,
        default_blueprint: companyExtended.default_blueprint,
        auto_run_instant_fit: companyExtended.auto_run_instant_fit,
        additional_web_search: companyExtended.additional_web_search,
        web_content_translation: companyExtended.web_content_translation,
      };

      setDetail(companyDetail);
    }
  }, [companyInfoList, companyExtended]);

  return {
    blueprints,
    behaviorKits,
    isLoadingExtended: isLoadingCompanyEx,
    isLoading: isLoadingCompanyInfo,
    data: detail,
    error: errorCompanyInfoList || errorCompanyExtended,
    mutate,
  };
}

export function useCompanyUpdate() {
  const { requestApi } = useAppContext();
  const updateCompany = async (
    companyUpdate: CompanyUpdateInput
  ): Promise<any> => {
    return requestApi
      .put(
        `${API_URL}/company/${companyUpdate.companyUUID}/update`,
        Object.fromEntries(companyUpdate.updates)
      )
      .then((response) => {
        return response.data;
      })
      .catch((e) => {
        throw Error(e);
      });
  };

  const mutationResult = useMutation(
    (companyUpdate: CompanyUpdateInput) => updateCompany(companyUpdate),
    {}
  );
  return mutationResult;
}

export function useGetCompanyEx() {
  const { requestApi } = useAppContext();

  const mutationResult = useMutation<CompanyEx, unknown, string>(
    (companyUUID: string) => companyInfoExFetch(companyUUID, requestApi),
    {}
  );

  return {
    ...mutationResult,
  };
}

const updateBehaviorKit = async (
  requestApi: RequestApi,
  behaviorKit: BehaviorKitUpdate
): Promise<BehaviorKit> => {
  behaviorKit.modified_by = HARDCODED_ACTOR_UUID;
  return requestApi
    .put(
      `${API_URL}/blueprintdal/behavior_kit/${behaviorKit.external_id}/ex`,
      behaviorKit
    )
    .then((response) => {
      return response.data;
    });
};

export function useDeleteBehaviorKit() {
  const { requestApi } = useAppContext();

  const deleteBehaviorKit = async (
    behaviorKitUUID: string
  ): Promise<BehaviorKit> => {
    return requestApi
      .delete(`${API_URL}/blueprintdal/behavior_kit/${behaviorKitUUID}`)
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (behaviorKitUUID: string) => deleteBehaviorKit(behaviorKitUUID),
    {}
  );
  return mutationResult;
}

export function useUpdateBehaviorKit() {
  const { requestApi } = useAppContext();

  const mutationResult = useMutation<BehaviorKit, unknown, BehaviorKitUpdate>(
    (behaviorKit: BehaviorKitUpdate) =>
      updateBehaviorKit(requestApi, behaviorKit),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useCreateBehaviorKit() {
  const { requestApi } = useAppContext();

  const createBehaviorKit = async (
    behaviorKit: BehaviorKit
  ): Promise<BehaviorKit> => {
    return requestApi
      .post(`${API_URL}/blueprintdal/behavior_kit`, {
        ...behaviorKit,
        modified_by: HARDCODED_ACTOR_UUID, //TODO:  remove this from the backend
      })
      .then((response) => {
        return response.data;
      });
  };

  const mutationResult = useMutation(
    (behaviorKit: BehaviorKit) => createBehaviorKit(behaviorKit),
    {}
  );

  return {
    ...mutationResult,
  };
}

export function useGetBehaviorKit() {
  const { requestApi } = useAppContext();

  async function getBehaviorKit(behaviorKitUUID: string): Promise<BehaviorKit> {
    return requestApi
      .get(`${API_URL}/blueprintdal/behavior_kit/${behaviorKitUUID}`)
      .then((response) => {
        return response.data;
      });
  }

  const mutationResult = useMutation(
    (behaviorKitUUID: string) => getBehaviorKit(behaviorKitUUID),
    {}
  );
  return mutationResult;
}

export function useGetAnalyticsData() {
  const { requestApi } = useAppContext();
  async function getAnalyticsData(
    input: CompanyMetricsRequest
  ): Promise<AnalyticsData> {
    if (isMocksEnabled()) {
      return getAnalyticsDataMockMultiDistribution(input);
    }
    return requestApi
      .post(`${API_URL}/company-metrics`, input)
      .then((response) => {
        return response.data;
      });
  }
  const mutationResult = useMutation(
    (input: CompanyMetricsRequest) => getAnalyticsData(input),
    {}
  );
  return mutationResult;
}

export function useReportsLookup() {
  const { requestApi } = useAppContext();
  async function reportsLookup(
    input: ReportsLookupRequest
  ): Promise<ReportsLookupResponse> {
    return requestApi
      .post(`${API_URL}/reports-lookup`, {
        company_uuids: input.company_uuids,
        start_date: input.start_date,
        finish_date: input.finish_date,
        page: input.page ?? 1,
        page_size: input.page_size ?? 50,
        ...(input.skip_count !== undefined && { skip_count: input.skip_count }),
      })
      .then((response) => response.data);
  }
  return useMutation((input: ReportsLookupRequest) => reportsLookup(input), {});
}

export interface ReportsLookupExportResponse {
  download_url: string;
  filename: string;
  expires_in_seconds: number;
}

/** Usage report export can run for a long time (paging + enrichment + S3). */
const REPORTS_LOOKUP_EXPORT_TIMEOUT_MS = 60_000;

export function useReportsLookupExport() {
  const { requestApi } = useAppContext();
  return useMutation(async (input: ReportsLookupExportRequest) => {
    const response = await requestApi.post(
      `${API_URL}/reports-lookup/export`,
      input,
      { timeout: REPORTS_LOOKUP_EXPORT_TIMEOUT_MS }
    );
    const data = response.data as ReportsLookupExportResponse;
    const link = document.createElement("a");
    link.href = data.download_url;
    link.download = data.filename;
    link.rel = "noopener noreferrer";
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, {});
}

export function useGetBehaviorsByBehaviorKitId() {
  const { requestApi } = useAppContext();

  async function getBehavior(
    behaviorKitUUID: string
  ): Promise<BehaviorInfoMinimal[]> {
    return requestApi
      .get(`${API_URL}/behaviors/behavior_kit_uuid/${behaviorKitUUID}`)
      .then((response) => {
        return response.data;
      });
  }

  const mutationResult = useMutation(
    (behaviorKitUUID: string) => getBehavior(behaviorKitUUID),
    {}
  );
  return mutationResult;
}

export function useVerifyBehaviorKitDelete() {
  const { requestApi } = useAppContext();
  async function verifyBehaviorKitDelete(
    behaviorKitUUID: string
  ): Promise<string[]> {
    if (isMocksEnabled()) {
      // return getBlueprintVerifyMock([]); // ok
      return getBlueprintVerifyMock(["Subscription (some_uuid)"]); // fail
    }
    return requestApi
      .get(`${API_URL}/behavior_kit/${behaviorKitUUID}/verify_delete`)
      .then((response) => {
        return response.data;
      });
  }
  const mutationResult = useMutation(
    (behaviorKitUUID: string) => verifyBehaviorKitDelete(behaviorKitUUID),
    {}
  );
  return mutationResult;
}

export interface VerifyBlueprintDeleteInput {
  blueprintUUID: string;
  companyUUID: string;
}

export function useVerifyBlueprintDelete() {
  const { requestApi } = useAppContext();
  async function verifyBlueprintDelete(
    blueprintUUID: string,
    companyUUID: string
  ): Promise<string[]> {
    if (isMocksEnabled()) {
      return getBlueprintVerifyMock([]); // ok
      // return getBlueprintVerifyMock(["Subscription (some_uuid)"]); // fail
    }
    return requestApi
      .get(
        `${API_URL}/company/${companyUUID}/blueprint/${blueprintUUID}/verify_delete`
      )
      .then((response) => {
        return response.data;
      });
  }
  const mutationResult = useMutation(
    (input: VerifyBlueprintDeleteInput) =>
      verifyBlueprintDelete(input.blueprintUUID, input.companyUUID),
    {}
  );
  return mutationResult;
}

export function getSettingTypeValueForCompanyCreationSetting(
  settingTypes: SettingType[] | undefined,
  creationSetting: CompanyCreationSetting[]
): CompanyCreationSetting[] {
  if (!settingTypes) return [];
  const creationSettingTypes = settingTypes.map((setting) => {
    let value: unknown = setting.default;
    const boolValues = { true: "true", false: "false" };

    const settingType = creationSetting.find(
      (st) => st.setting_type_uuid === setting.external_id
    );

    if (setting.name === SETTING_TYPES_ENUM.CAN_CREATE_COMPANY) {
      value = settingType?.value ? boolValues.true : boolValues.false;
    }
    // Warning: if other settings values come with an state update accordingly

    return { setting_type_uuid: setting.external_id, value };
  });
  return creationSettingTypes;
}

function isBoolSettingDataType(dataType: string) {
  return dataType.toLocaleUpperCase() === "BOOL";
}

export function getSettingValue(
  name: SETTING_TYPES_ENUM,
  settingTypes: SettingType[] | undefined,
  companySettings: CompanyCreationSetting[]
): boolean {
  const settingType = settingTypes?.find((item) => item.name === name);
  if (!settingType) return false;
  const foundValue = companySettings.find(
    (item) => item.setting_type_uuid === settingType?.external_id
  )?.value;

  if (typeof foundValue === "undefined") {
    if (isBoolSettingDataType(settingType.data_type)) {
      return settingType.default.toLocaleLowerCase() === "true";
    }
  }
  if (typeof foundValue === "number") {
    if (isBoolSettingDataType(settingType.data_type))
      return settingType.default.toLocaleLowerCase() === "true";
  }
  if (isBoolSettingDataType(settingType.data_type)) {
    if (typeof foundValue === "string")
      return foundValue.toLocaleLowerCase() === "true";

    return !!foundValue;
  }

  return false;
}

async function fileToBase64(file: File): Promise<string> {
  const promise = new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = async (event) => {
      if (event.target && event.target.result) {
        const base64String = (event.target.result as string).split(",")[1];
        resolve(base64String);
      }
    };

    reader.onerror = (error) => {
      reject(error);
    };

    reader.readAsDataURL(file);
  });
  return promise;
}

export function useImageToBase64() {
  const logoToBase64 = async (file: File): Promise<any> => {
    return fileToBase64(file);
  };

  const mutationResult = useMutation((input: File) => logoToBase64(input), {});

  return {
    ...mutationResult,
  };
}

export function useCreateSamplePDF() {
  const { requestApi } = useAppContext();

  async function createSamplePDF(input: {
    company_uuid: string;
    template_info: PDFTemplate;
  }): Promise<string> {
    return requestApi
      .post(
        `${API_URL}/company_sample_pdf?company_uuid=${input.company_uuid}`,
        input.template_info
      )
      .then((response) => {
        if (response.data.report_url) {
          resolveAndDownload(response.data.report_url);
        }
        return "";
      });
  }

  const mutationResult = useMutation(
    (input: { company_uuid: string; template_info: PDFTemplate }) =>
      createSamplePDF(input),
    {}
  );
  return mutationResult;
}

export function useGoToCompany() {
  const navigate = useNavigate();

  const goToCompany = (
    companyUUID: string,
    tab: string = "general",
    skipSettings: boolean = false
  ) => {
    const settingsPath = skipSettings ? "" : "settings/";
    navigate(`/companies/${companyUUID}/${settingsPath}${tab}`);
  };

  const goToSubCompany = (
    companyUUID: string,
    subCompanyUUID: string,
    tab: string = "general"
  ) => {
    navigate(
      `/companies/${companyUUID}/subcompany/${subCompanyUUID}/settings/${tab}`
    );
  };

  return {
    goToCompany,
    goToSubCompany,
  };
}
